import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import cx from "classnames";
import withStyles from "isomorphic-style-loader/withStyles";
import { formValueSelector, getFormSyncErrors } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import Container from 'react-bootstrap/Container';
import { Modal } from 'react-bootstrap';
import SafetyMeasure from "../SafetyMeasures/SafetyMeasures";
import BookingForm from "../BookingForm/BookingForm";
import getPaymentMethodsQuery from '../../components/BookingDetail/PriceDetails/PaymentMethod/getAllPaymentMethods.graphql';
import CommonModal from "../Modal/CommonModal";
import SocketContext from "../../core/socketNotifications/SocketContext";
import NearestProviderForm from "../NearestProviderForm/NearestProviderForm";
import ScheduleBookingForm from "../ScheduleBookingForm/ScheduleBookingForm";
import { closeHeaderModal, openHeaderModal } from "../../actions/siteadmin/modalActions";
import { changeBookingError } from "../../actions/booking/changeBookingError";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import messages from "../../locale/messages";
import scheduleDateSplitUp from "../../helpers/scheduleDateSplitUp";
import ProviderSelectionForm from "../ProviderSelectionForm/ProviderSelectionForm";
import PreferredProviderForm from "../PreferredProviderForm/PreferredProviderForm";

import getAllPrecautionNotification from './getAllPrecautionNotification.graphql';
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import bt from '../commonStyle.css';
import s from "./ConfirmBookingBanner.css";

const ConfirmBookingBanner = ({ loading, data, intl: { formatMessage } }) => {

    const { data: precautionNotification, loading: precautionLoading } = useQueryResponse({
        query: getAllPrecautionNotification,
        fetchPolicy: 'network-only',
        ssr: false
    })

    const { data: paymentMethods, loading: paymentMethodLoading } = useQueryResponse({
        query: getPaymentMethodsQuery,
        fetchPolicy: 'network-only',
        ssr: true
    })
    const dispatch = useDispatch()
    // "How payment works" dialog — shown BEFORE the booking is placed (mobile parity with the
    // user app's view_cart _showPaymentInfoDialog). ADDITIVE: surfaced in front of the existing
    // handleClick booking trigger; "Got it / Continue" runs the unchanged handleClick.
    const [showPaymentInfo, setShowPaymentInfo] = useState(false)
    const {
        safetyMeasureForm,
        bookingForm,
        nearestProviderForm,
        scheduleBookingForm,
        bookingError,
        paymentMethodId,
        walletAmount,
        userCardPaymentMethodId,
        fieldErrors,
        fieldErrors2,
        scheduleFrom,
        paypalCurrency,
        providerSelectionForm,
        preferredProviderForm,
        isPreffered
    } = useSelector((state) => {
        const paymentSelector = formValueSelector("PaymentMethod");
        const scheduleSelector = formValueSelector('ScheduleBookingForm');
        return {
            safetyMeasureForm: state.adminModalStatus.safetyMeasureForm,
            bookingForm: state.adminModalStatus.bookingForm,
            nearestProviderForm: state.adminModalStatus.nearestProviderForm,
            scheduleBookingForm: state.adminModalStatus.scheduleBookingForm,
            bookingError: state?.booking?.data?.error,
            paymentMethodId: paymentSelector(state, 'paymentMethodId'),
            walletAmount: paymentSelector(state, 'walletAmount'),
            userCardPaymentMethodId: paymentSelector(state, 'userCardPaymentMethodId'),
            fieldErrors: getFormSyncErrors('OrderDetails')(state),
            scheduleFrom: scheduleSelector(state, 'scheduleFrom'),
            paypalCurrency: paymentSelector(state, 'paypalCurrency'),
            preferredProviderForm: state.adminModalStatus.preferredProviderForm,
            providerSelectionForm: state.adminModalStatus.providerSelectionForm,
            isPreffered: state?.adminModalStatus?.isPreffered
        };

    });

    const closeSafetyMeasureModal = () => {
        dispatch(closeHeaderModal('safetyMeasureForm'));
    }

    const closeBookingFormModal = () => {
        dispatch(closeHeaderModal('bookingForm'));
    }
    const closeNearestProviderFormModal = () => {
        // Parity with the app: the search/connecting screen is non-dismissible and NEVER dumps the
        // client to the bookings list. Legitimate exits already navigate themselves (accept / on-board
        // -> /tracking, explicit Cancel -> cancelBookingAction). So closing here is non-destructive:
        // just clear the modal + any stale error, no redirect. (Was: window.location.replace to
        // /bookings/upcoming, which made the Choose-for-me waiting screen vanish on validate.)
        if (isPreffered) dispatch(closeHeaderModal('preferredProviderForm'))
        dispatch(closeHeaderModal('nearestProviderForm'));
        changeBookingError();
    }
    const closeScheduleBookingFormModal = () => {
        dispatch(closeHeaderModal('scheduleBookingForm'));
    }

    const closeproviderSelectionForm = () => {
        dispatch(closeHeaderModal('providerSelectionForm'))
    }

    const closepreferredProviderModal = () => {
        dispatch(closeHeaderModal('preferredProviderForm'))
    }

    // Modal-Back (resumable): the booking steps (flow choice / date / payment / provider list) are
    // modals on /order/:id with no URL of their own, so a browser/gesture Back used to pop the whole
    // route off /order. Instead: when a DISMISSIBLE booking modal opens, push a throwaway history entry;
    // a subsequent Back fires popstate, which we intercept to CLOSE the open modal (returning to the
    // previous in-page step) rather than leaving the page. The non-dismissible "connecting" screen
    // (nearestProviderForm — static backdrop) is deliberately excluded so Back can't kill it.
    const backCloseableOpen =
        safetyMeasureForm || bookingForm || providerSelectionForm || preferredProviderForm || scheduleBookingForm;
    const backStatePushedRef = useRef(false);

    const closeTopMostModal = () => {
        // Close the single front-most dismissible modal, mirroring the in-flow step order.
        if (safetyMeasureForm) { closeSafetyMeasureModal(); return; }
        if (bookingForm) { closeBookingFormModal(); return; }
        if (scheduleBookingForm) { closeScheduleBookingFormModal(); return; }
        if (providerSelectionForm) { closeproviderSelectionForm(); return; }
        if (preferredProviderForm) { closepreferredProviderModal(); return; }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (backCloseableOpen && !backStatePushedRef.current) {
            // Add a history entry to "absorb" the next Back so it pops this state, not the /order route.
            try { window.history.pushState({ bhBookingModal: true }, ''); } catch (e) { /* ignore */ }
            backStatePushedRef.current = true;
        }
        const onPopState = () => {
            // Back consumed our pushed entry. If a dismissible modal is open, swallow it: just close the
            // front-most modal. We DON'T re-push here — the open-effect re-arms automatically on the next
            // render if another step is still open (backStatePushedRef was reset), so no orphan entries.
            if (backStatePushedRef.current) {
                backStatePushedRef.current = false;
                if (backCloseableOpen) closeTopMostModal();
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [backCloseableOpen, safetyMeasureForm, bookingForm, providerSelectionForm, preferredProviderForm, scheduleBookingForm]);

    // When all dismissible modals are closed via the normal close path (X / backdrop / Continue), drop
    // the guard flag so we don't leave a dangling pushed state armed.
    useEffect(() => {
        if (!backCloseableOpen) backStatePushedRef.current = false;
    }, [backCloseableOpen]);

    const handleClick = () => {
        let precautionDetails;
        if (precautionNotification?.getAllPrecautionNotification?.results?.length > 0) {
            precautionDetails = precautionNotification?.getAllPrecautionNotification.results[0];
        }
        const shouldOpenSafetyMeasure = precautionDetails?.isEnabled;
        const selectedPaymentMethod = paymentMethods.getAllPaymentMethods.results.find(method => method.paymentType == paymentMethodId);
        if (paymentMethodId == 5 && !paypalCurrency) {
            showToaster({ messageId: 'selectCurrency', toasterType: 'error' })
            return;
        }
        if (fieldErrors?.description?.defaultMessage) {
            return;
        }
        if (paymentMethodId == 4 || (selectedPaymentMethod && !selectedPaymentMethod?.isEnable)) {
            showToaster({ messageId: 'noPaymentType', toasterType: 'error' });
            return;
        }
        if (paymentMethodId == 3 && (walletAmount == 0 || walletAmount < data?.result?.userPayableFare)) {
            showToaster({ messageId: 'noWalletAmount', toasterType: 'error' });
            return;
        }
        if (paymentMethodId == 2 && (!userCardPaymentMethodId || userCardPaymentMethodId == 0)) {
            showToaster({ messageId: 'noCardDetails', toasterType: 'error' });
            return;
        }

        dispatch(openHeaderModal(shouldOpenSafetyMeasure ? 'safetyMeasureForm' : 'bookingForm'))

    }

    // Confirm button now first surfaces the "How payment works" dialog. Proceeding runs the
    // unchanged handleClick (validations + booking sequence stay exactly as before).
    const proceedBooking = () => {
        setShowPaymentInfo(false);
        handleClick();
    }

    let precautionDetails, formData, safetyMeasuresImage, safetyMeasuresTitle, scheduleInitialValues;
    if (precautionNotification?.getAllPrecautionNotification?.results?.length > 0) {
        precautionDetails = precautionNotification?.getAllPrecautionNotification?.results[0]
    }
    formData = {
        bookingData: data?.result
    }

    scheduleInitialValues = scheduleDateSplitUp(scheduleFrom);

    const shouldOpenSafetyMeasure = precautionDetails?.isEnabled;
    safetyMeasuresTitle = precautionDetails?.title;
    safetyMeasuresImage = precautionDetails?.imageName;

    return (
        <>
            <CommonModal
                title={
                    safetyMeasureForm
                        ? safetyMeasuresTitle
                        : bookingForm ?
                            formatMessage(messages.bookingTitle)
                            : providerSelectionForm
                                ? formatMessage(messages.selectProviderTitle)
                                : preferredProviderForm
                                    ? formatMessage(messages.preferredProviderTitle)
                                    : scheduleBookingForm
                                        ? formatMessage(messages.scheduleYourBooking)
                                        : ''
                }
                onHide={
                    safetyMeasureForm
                        ? closeSafetyMeasureModal
                        : bookingForm
                            ? closeBookingFormModal
                            : providerSelectionForm
                                ? closeproviderSelectionForm
                                : preferredProviderForm
                                    ? closepreferredProviderModal
                                    : nearestProviderForm
                                        ? closeNearestProviderFormModal
                                        : closeScheduleBookingFormModal
                }
                modalStatus={
                    safetyMeasureForm
                        ? safetyMeasureForm
                        : bookingForm ? bookingForm
                            : providerSelectionForm
                                ? providerSelectionForm
                                : preferredProviderForm
                                    ? preferredProviderForm
                                    : nearestProviderForm
                                        ? nearestProviderForm
                                        : scheduleBookingForm
                }

                isBookingBanner={safetyMeasureForm && true}
                safetyMeasuresImage={safetyMeasuresImage}
                /* Search/connecting screen = non-dismissible (parity with the app): no backdrop close,
                   no ESC, no X — so the Choose-for-me waiting screen can't vanish. The client leaves it
                   only via accept / on-board (-> /tracking) or the in-screen Cancel link. */
                backdrop={nearestProviderForm ? 'static' : undefined}
                keyboard={nearestProviderForm ? false : undefined}
                noCloseIcon={nearestProviderForm ? true : undefined}
            >
                {
                    safetyMeasureForm ? (
                        <SafetyMeasure precautionDetails={precautionDetails} />
                    ) : bookingForm ? (
                        <BookingForm
                            formData={formData}
                            shouldOpenSafetyMeasure={shouldOpenSafetyMeasure}
                        />
                    ) : providerSelectionForm ? (
                        <>
                            <ProviderSelectionForm formData={formData} />
                        </>
                    ) : nearestProviderForm ? (
                        <SocketContext.Consumer>
                            {socket => <NearestProviderForm socket={socket} />}
                        </SocketContext.Consumer>
                    ) : preferredProviderForm ? (
                        <SocketContext.Consumer>
                            {socket => <PreferredProviderForm formData={formData} socket={socket} />}
                        </SocketContext.Consumer>
                    ) : (
                        <ScheduleBookingForm
                            formData={formData}
                            initialValues={scheduleInitialValues}
                        />
                    )
                }

            </CommonModal >
            <div className={s.reviewYourRequestContainer}>
                <Container>
                    <div className={s.reviewYourRequestWrapper}>
                        <div onClick={() => setShowPaymentInfo(true)} className={cx(bt.btnPrimary, bt.cursorPointer, 'loaderButtonFlex')}><FormattedMessage {...messages.confirmBooking} /> </div>
                    </div>
                </Container>
            </div>
            <Modal
                show={showPaymentInfo}
                animation={false}
                onHide={() => setShowPaymentInfo(false)}
                dialogClassName={'commonModalContainer'}
            >
                <Modal.Header closeButton className={'commonModalHeader'}>
                    <Modal.Title>
                        <div className={'commonModalTitle'}>
                            <span><FormattedMessage {...messages.howPaymentWorksTitle} /></span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={'commonModalBody'}>
                    <div>
                        <ul className={cx(bt.commonSize14, bt.space3)}>
                            <li className={bt.space2}><FormattedMessage {...messages.paymentInfo1} /></li>
                            <li className={bt.space2}><FormattedMessage {...messages.paymentInfo2} /></li>
                            <li className={bt.space2}><FormattedMessage {...messages.paymentInfo3} /></li>
                            <li className={bt.space2}><FormattedMessage {...messages.paymentInfo4} /></li>
                        </ul>
                        <div onClick={proceedBooking} className={cx(bt.btnPrimary, bt.cursorPointer, 'loaderButtonFlex')}>
                            <FormattedMessage {...messages.howPaymentWorksContinue} />
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default injectIntl(withStyles(bt, s)(ConfirmBookingBanner));
